/**
 * 头像压缩工具
 * 用于将全尺寸头像压缩为 48x48 像素的缩略图，供推送通知使用
 *
 * 使用 createImageBitmap + OffscreenCanvas 进行异步解码和压缩，
 * 避免阻塞主线程（对于 50+ 联系人批量压缩场景至关重要）
 */

/**
 * 压缩头像为 48x48 像素的 data URL，用于推送通知
 * @param avatarDataUrl 原始头像 data URL（可能几百 KB）
 * @returns 压缩后的 data URL（目标 < 2KB）或 undefined
 */
export async function compressAvatarForPush(avatarDataUrl: string | undefined): Promise<string | undefined> {
  if (!avatarDataUrl)
    return undefined

  try {
    // 使用 fetch + createImageBitmap 异步解码（不阻塞主线程）
    const response = await fetch(avatarDataUrl)
    const blob = await response.blob()

    // createImageBitmap 自带 resize 选项，性能最优
    const bitmap = await createImageBitmap(blob, {
      resizeWidth: 48,
      resizeHeight: 48,
      resizeQuality: 'high',
    })

    // 使用 OffscreenCanvas（如果支持）或降级到普通 Canvas
    let canvas: OffscreenCanvas | HTMLCanvasElement
    let ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null

    if (typeof OffscreenCanvas !== 'undefined') {
      canvas = new OffscreenCanvas(48, 48)
      ctx = canvas.getContext('2d')
    }
    else {
      // 降级：在不支持 OffscreenCanvas 的环境（如某些旧浏览器）
      canvas = document.createElement('canvas')
      canvas.width = 48
      canvas.height = 48
      ctx = canvas.getContext('2d')
    }

    if (!ctx) {
      bitmap.close()
      return undefined
    }

    ctx.drawImage(bitmap, 0, 0)
    bitmap.close()

    // 尝试 WebP 压缩
    let compressedBlob: Blob
    if (canvas instanceof OffscreenCanvas) {
      compressedBlob = await canvas.convertToBlob({ type: 'image/webp', quality: 0.8 })

      // 如果 WebP 仍超 3KB，降级到 PNG
      if (compressedBlob.size > 3000) {
        compressedBlob = await canvas.convertToBlob({ type: 'image/png' })
      }
    }
    else {
      // HTMLCanvasElement 降级路径
      const dataUrl = canvas.toDataURL('image/webp', 0.8)
      if (dataUrl.length > 4000) {
        const pngDataUrl = canvas.toDataURL('image/png')
        if (pngDataUrl.length > 4000) {
          console.warn('[AvatarCompression] 压缩后仍超 4KB，丢弃头像')
          return undefined
        }
        return pngDataUrl
      }
      return dataUrl
    }

    // 最终检查大小
    if (compressedBlob.size > 4000) {
      console.warn('[AvatarCompression] 压缩后仍超 4KB，丢弃头像')
      return undefined
    }

    // 转换为 data URL
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = () => resolve(undefined)
      reader.readAsDataURL(compressedBlob)
    })
  }
  catch (err) {
    console.warn('[AvatarCompression] 头像压缩失败:', err)
    return undefined
  }
}
